
import { computed, defineComponent, PropType, reactive, onBeforeMount } from 'vue'

// @components
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'

// @types
import {
    DispatcherSector,
    DispatcherSectorFormPayload,
} from '@/new-services/types/dispatcher-sectors'
import { CityT } from '@/core/types/cities.types'

// @validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @realisations
import { cities } from '@/core/realisations/cities.realisation'
import { users } from '@/core/realisations/users.realisation'

export default defineComponent({
    name: 'new-dispatcher-sector-form',
    components: {
        NewInput,
        NewSelect,
        NewButton,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<DispatcherSector>,
            default: null,
        },
        isPaintStarted: {
            type: Boolean,
            default: false,
        },
        isSectorAdded: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel', 'start-paint', 'create', 'update'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            name: null as string | null,
            description: null as string | null,
            dispatcherIds: [] as number[],
            cityId: null as number | null,
        })

        const state_form$ = useVuelidate(
            {
                name: { required },
                description: { required },
                dispatcherIds: { required },
                cityId: { required },
            },
            state
        )

        const cities_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CityT[],
        })

        const managers_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CityT[],
        })

        const loadCities = async () => {
            try {
                cities_state.is_loading = true

                const data = await cities.getAllList(1)
                cities_state.list = data.data

                cities_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })

                cities_state.is_failed = true
            } finally {
                cities_state.is_loading = false
            }
        }

        const loadDispatchers = async () => {
            try {
                managers_state.is_loading = true

                const params = {
                    page: 1,
                    limit: 9999,
                    roleIds: [16, 40],
                }

                const data = await users.getList(params)
                managers_state.list = data.data

                managers_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка менеджеров произошла ошибка',
                })

                managers_state.is_failed = true
            } finally {
                managers_state.is_loading = false
            }
        }

        const onCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const onStartPaint = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('start-paint', {
                description: state.description,
            })
        }

        const emitCreate = () => {
            const payload: Omit<DispatcherSectorFormPayload, 'coordinates'> = {
                name: state.name as string,
                description: state.description as string,
                dispatcherIds: state.dispatcherIds as number[],
                cityId: state.cityId as number,
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            const payload: Omit<DispatcherSectorFormPayload, 'coordinates'> = {
                name: state.name as string,
                description: state.description as string,
                dispatcherIds: state.dispatcherIds as number[],
                cityId: state.cityId as number,
            }

            emit('update', {
                id: props.initialValue.id,
                payload,
            })
        }

        const onSubmit = () => {
            if (props.loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                emitCreate()
            }

            if (props.isUpdate) {
                emitUpdate()
            }
        }

        onBeforeMount(() => {
            loadCities()
            loadDispatchers()

            if (props.isUpdate && props.initialValue) {
                state.name = props.initialValue.name
                state.description = props.initialValue.description

                state.cityId = props.initialValue.cityId
                state.dispatcherIds = props.initialValue.dispatcherIds
            }
        })

        const modalTitle = computed(() => {
            if (props.isCreate) {
                return 'Добавление сектора диспетчера'
            }

            if (props.isUpdate) {
                return 'Редактирование сектора диспетчера'
            }

            return 'modal title'
        })

        const buttonTitle = computed(() => {
            if (props.isUpdate) {
                return 'Сохранить сектор'
            }

            if (props.isCreate) {
                return 'Добавить сектор'
            }

            return 'button title'
        })

        const isRenderingStartPaint = computed(() => {
            if (props.isUpdate) return false

            if (props.isPaintStarted || props.isSectorAdded) {
                return false
            }

            return true
        })

        const isRenderingOnPaint = computed(() => {
            if (props.isUpdate) return false

            if (props.isPaintStarted && !props.isSectorAdded) {
                return true
            }

            return false
        })

        const isRenderingSubmit = computed(() => {
            if (props.isUpdate) return true

            if (!props.isPaintStarted && props.isSectorAdded) {
                return true
            }

            return false
        })

        return {
            state,
            state_form$,
            //
            cities_state,
            loadCities,
            managers_state,
            loadDispatchers,
            //
            onCancel,
            onStartPaint,
            onSubmit,
            //
            modalTitle,
            buttonTitle,
            isRenderingStartPaint,
            isRenderingOnPaint,
            isRenderingSubmit,
        }
    },
})
